import { useMemo } from 'react'
import { Users } from '@phosphor-icons/react'
import { Formik } from 'formik'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useGetBuildingPlaces } from '@/api/clients/get-building-places'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { NumericInput } from '@/components/ui/numeric-input'

import Loader from '../../Loader/Loader'
import ModalContainer from '../../ModalContainer/ModelContainer'
import {
  getAgeStringTranslated,
  useMemoizedPoolSelectOptions,
  shouldDisplayPoolInfo,
} from '../helpers'

import { displayUserFullName, getValidationSchema } from './helpers'
import { useAddGuestPassDetails, useGetGuestPassDetails } from './hooks'
import { HouseholdMember } from './HouseholdMember'

export const VisitCreateModal = ({ addGuestPass, amenityClientId, clientId, onClose }) => {
  const { t } = useTranslation(['clients-legacy', 'common'])
  const selectedAppBuilding = useSelector(selectActiveBuilding)
  const selectedBuilingId = selectedAppBuilding?.id

  // Note: `amenityClientId` is the clientId of the amenity that the guest is visiting
  const { data, isLoading } = useGetGuestPassDetails(amenityClientId)

  const { data: places, isFetching: isPlacesFetching } = useGetBuildingPlaces(selectedBuilingId)

  const isPoolInfoAvailable = shouldDisplayPoolInfo(selectedBuilingId)
  const poolSelectOptions = useMemoizedPoolSelectOptions(places)

  const INITIAL_VALUES = {
    guestCount: 0,
    householdMembersIds: [],
    ...(isPoolInfoAvailable && {
      pool: poolSelectOptions[0],
    }),
  }

  const onCallback = async (newItem) => {
    await addGuestPass(newItem)
    onClose()
  }

  const { isLoading: isSubmitting, fetchCreateGuestPasses } = useAddGuestPassDetails(
    clientId,
    onCallback,
    isPoolInfoAvailable,
    t,
  )

  const validationSchema = useMemo(() => getValidationSchema(t), [t])

  return (
    <ModalContainer
      additionalClass="visit-details-modal-container"
      onClose={onClose}
      headerTitle={t('clients-legacy:visitCreate.headerTitle')}
      headerIcon={<Users size={20} />}
    >
      <Formik
        enableReinitialize
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={fetchCreateGuestPasses}
      >
        {({ handleSubmit, values, errors, handleChange }) => {
          if ((isLoading && !data) || isPlacesFetching) {
            return <Loader />
          }

          if (!data) {
            return (
              <div className="visit-details-container">
                <div className="visit-details-header">
                  {t('clients-legacy:visitCreate.guestPassNotFound')}
                </div>
              </div>
            )
          }

          const { householdMembersIds, guestCount, pool } = values
          const { client, clients = [] } = data

          const handleHouseholdMemberChange = ({ id, checked }) => {
            if (checked) {
              handleChange({
                target: {
                  name: 'householdMembersIds',
                  value: [...householdMembersIds, id],
                },
              })
              return
            }

            handleChange({
              target: {
                name: 'householdMembersIds',
                value: householdMembersIds.filter((memberId) => memberId !== id),
              },
            })
          }

          const handleGuestCountChange = (value) => {
            handleChange({ target: { name: 'guestCount', value } })
          }

          const handleIncrement = () => {
            handleChange({ target: { name: 'guestCount', value: Number(guestCount) + 1 } })
          }

          const handleDecrement = () => {
            if (Number(guestCount) > 0) {
              handleChange({ target: { name: 'guestCount', value: Number(guestCount) - 1 } })
            }
          }

          const handlePoolChange = (value) => {
            handleChange({
              target: {
                name: 'pool',
                value,
              },
            })
          }

          return (
            <form className="visit-details-container" onSubmit={handleSubmit}>
              {isPoolInfoAvailable && (
                <Select
                  className="select-container"
                  classNamePrefix="select-primary"
                  name="pool"
                  options={poolSelectOptions}
                  onChange={handlePoolChange}
                  value={pool}
                  selectStyle={{ width: '100%' }}
                  isDisabled={isPlacesFetching}
                />
              )}
              <div className="visit-details-header">
                {t('clients-legacy:visitCreate.passInformation')}
              </div>
              <div className="visit-details-table-wrapper">
                <div className="visit-row-container">
                  <div>{t('clients-legacy:visitCreate.residentLabel')}</div>
                  <div>{displayUserFullName(client.user)}</div>
                </div>
                <div className="visit-row-container">
                  <div>{t('clients-legacy:visitCreate.amenityUniqueIdLabel')}</div>
                  <div>{amenityClientId}</div>
                </div>
                <div className="visit-row-container">
                  <div>{t('clients-legacy:table.columns.phoneNumber')}</div>
                  <div>{client.phone}</div>
                </div>
                <div className="visit-row-container">
                  <div>{t('clients-legacy:newClientForm.addressPlaceholder')}</div>
                  <div>{client.street}</div>
                </div>
                <div className="visit-row-container">
                  <div>{t('clients-legacy:visitCreate.ageLabel')}</div>
                  <div>{getAgeStringTranslated(client.birth, t)}</div>
                </div>
              </div>
              {clients?.length > 0 && (
                <>
                  <div className="visit-details-header">
                    {t('clients-legacy:visitCreate.householdMembers')}
                  </div>
                  <div className="visit-create-table-wrapper">
                    {clients.map((client) => (
                      <HouseholdMember
                        clientId={client.id}
                        isChecked={householdMembersIds.includes(client.id)}
                        isLoading={isSubmitting}
                        key={client.id}
                        user={client.user}
                        userBirth={client.birth}
                        onChange={handleHouseholdMemberChange}
                      />
                    ))}
                  </div>
                </>
              )}

              <div className="input-container">
                <div className="input-label">
                  {t('clients-legacy:visitCreate.guestPassesToRedeemLabel')}
                </div>
                <Flex gap={8} align="center">
                  <NumericInput
                    size="small"
                    min={0}
                    value={String(guestCount)}
                    onChange={handleGuestCountChange}
                  />
                  <Button
                    use="secondary"
                    size="small"
                    onlyBox
                    type="button"
                    onClick={handleDecrement}
                  >
                    -
                  </Button>
                  <Button
                    use="secondary"
                    size="small"
                    onlyBox
                    type="button"
                    onClick={handleIncrement}
                  >
                    +
                  </Button>
                </Flex>
              </div>
              {errors.guestCount ? (
                <label className="help is-danger">{errors.guestCount}</label>
              ) : null}
              <div>
                <div className="visit-create-actions">
                  <button className="button button-gray" onClick={onClose} disabled={isSubmitting}>
                    {t('common:actions.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="button button-primary auto-width"
                    disabled={isSubmitting}
                  >
                    {t('clients-legacy:visitCreate.checkInButton')}
                  </button>
                </div>
              </div>
            </form>
          )
        }}
      </Formik>
    </ModalContainer>
  )
}

VisitCreateModal.propTypes = {
  addGuestPass: PropTypes.func,
  amenityClientId: PropTypes.string,
  clientId: PropTypes.number,
  onClose: PropTypes.func,
  timezone: PropTypes.string,
}
